/**
 * Authoring helpers that expand convenient shapes into valid `SolidDef`s.
 *
 * Rapier's single-precision queries lose accuracy as a collider grows (measured
 * on Rapier 0.21: ~0.15 mm distance error for a 10 m box, ~4 cm for a 400 m
 * box). Levels therefore cap collider size (MAX_COLLIDER_HALF_EXTENT) and large
 * surfaces are tiled. Tile seams are covered by the collision seam tests.
 */
import type { SolidDef, Vec3Tuple } from './LevelData';

export const MAX_COLLIDER_HALF_EXTENT = 10;

/** Splits an axis-aligned box into tiles no larger than `maxHalf` per axis. */
export function tiledBox(idPrefix: string, center: Vec3Tuple, half: Vec3Tuple, material: string, maxHalf = 8, tag?: string): SolidDef[] {
  const counts = half.map((h) => Math.max(1, Math.ceil(h / maxHalf))) as [number, number, number];
  const tileHalf = half.map((h, i) => h / counts[i]!) as [number, number, number];
  const out: SolidDef[] = [];
  for (let ix = 0; ix < counts[0]; ix++) {
    for (let iy = 0; iy < counts[1]; iy++) {
      for (let iz = 0; iz < counts[2]; iz++) {
        const c: [number, number, number] = [
          center[0] - half[0] + tileHalf[0] * (2 * ix + 1),
          center[1] - half[1] + tileHalf[1] * (2 * iy + 1),
          center[2] - half[2] + tileHalf[2] * (2 * iz + 1),
        ];
        out.push({ id: `${idPrefix}.${ix}.${iy}.${iz}`, shape: { type: 'box', halfExtents: tileHalf }, position: c, material, ...(tag ? { tag } : {}) });
      }
    }
  }
  return out;
}

/**
 * A straight ramp whose top surface runs from `from` to `to` (top-surface
 * points). The ramp must run along X or Z (the other horizontal coordinate
 * equal); it is split into segments to respect MAX_COLLIDER_HALF_EXTENT.
 */
export function ramp(idPrefix: string, from: Vec3Tuple, to: Vec3Tuple, width: number, thickness: number, material: string): SolidDef[] {
  const alongX = from[2] === to[2];
  if (!alongX && from[0] !== to[0]) throw new Error(`ramp ${idPrefix}: must run along X or Z`);
  const dh = alongX ? to[0] - from[0] : to[2] - from[2];
  const dy = to[1] - from[1];
  const length = Math.hypot(dh, dy);
  const segments = Math.max(1, Math.ceil(length / 2 / 8));
  // Unit vector from `to` back to `from` defines the box's local +X (or +Z) axis.
  const ux = -dh / length;
  const uy = -dy / length;
  const theta = alongX ? Math.atan2(uy, ux) : Math.atan2(-uy, ux);
  // Top-surface normal of the rotated box.
  const nh = alongX ? -Math.sin(theta) : Math.sin(theta);
  const ny = Math.cos(theta);
  const out: SolidDef[] = [];
  for (let i = 0; i < segments; i++) {
    const t = (i + 0.5) / segments;
    const top: [number, number, number] = [from[0] + (to[0] - from[0]) * t, from[1] + dy * t, from[2] + (to[2] - from[2]) * t];
    const half = length / segments / 2;
    const center: [number, number, number] = alongX
      ? [top[0] - nh * (thickness / 2), top[1] - ny * (thickness / 2), top[2]]
      : [top[0], top[1] - ny * (thickness / 2), top[2] - nh * (thickness / 2)];
    out.push({
      id: `${idPrefix}.${i}`,
      shape: { type: 'box', halfExtents: alongX ? [half, thickness / 2, width / 2] : [width / 2, thickness / 2, half] },
      position: center,
      rotationDeg: alongX ? [0, 0, (theta * 180) / Math.PI] : [(theta * 180) / Math.PI, 0, 0],
      material,
    });
  }
  return out;
}

/**
 * Approximates a vertical curved wall (arc of a circle in the XZ plane) with
 * `segments` boxes. Angles in degrees, measured from +X toward -Z.
 */
export function arcWall(
  idPrefix: string,
  center: Vec3Tuple,
  radius: number,
  fromDeg: number,
  toDeg: number,
  segments: number,
  height: number,
  thickness: number,
  material: string,
): SolidDef[] {
  const out: SolidDef[] = [];
  const span = ((toDeg - fromDeg) * Math.PI) / 180;
  const step = span / segments;
  // Segment length slightly overlaps neighbours so the inner surface has no gaps.
  const halfLen = radius * Math.tan(Math.abs(step) / 2) + thickness * 0.5;
  for (let i = 0; i < segments; i++) {
    const a = (fromDeg * Math.PI) / 180 + step * (i + 0.5);
    const r = radius + thickness / 2;
    const x = center[0] + Math.cos(a) * r;
    const z = center[2] - Math.sin(a) * r;
    out.push({
      id: `${idPrefix}.${i}`,
      shape: { type: 'box', halfExtents: [thickness / 2, height / 2, halfLen] },
      position: [x, center[1] + height / 2, z],
      rotationDeg: [0, (a * 180) / Math.PI, 0],
      material,
    });
  }
  return out;
}

/**
 * Approximates a concave curved floor (quarter-pipe / bowl profile) in the YZ
 * plane: boxes tangent to a circle of `radius` whose centre is `center`.
 * Angle 0 is the lowest point of the circle (directly below the centre);
 * positive angles climb toward -Z. The curve's inner surface is walkable up
 * to the motor's max ground angle and becomes wall beyond it.
 */
export function concaveCurve(
  idPrefix: string,
  center: Vec3Tuple,
  radius: number,
  fromDeg: number,
  toDeg: number,
  segments: number,
  width: number,
  thickness: number,
  material: string,
): SolidDef[] {
  const out: SolidDef[] = [];
  const step = ((toDeg - fromDeg) * Math.PI) / 180 / segments;
  const halfLen = radius * Math.tan(Math.abs(step) / 2) + thickness * 0.5;
  for (let i = 0; i < segments; i++) {
    const a = (fromDeg * Math.PI) / 180 + step * (i + 0.5);
    const r = radius + thickness / 2;
    out.push({
      id: `${idPrefix}.${i}`,
      shape: { type: 'box', halfExtents: [width / 2, thickness / 2, halfLen] },
      position: [center[0], center[1] - Math.cos(a) * r, center[2] - Math.sin(a) * r],
      // Local +Y (the walkable face normal) points at the circle centre.
      rotationDeg: [(a * 180) / Math.PI, 0, 0],
      material,
    });
  }
  return out;
}
